import React from 'react'
import propTypes from 'prop-types'
import { Box, Button, TextField, Typography, makeStyles } from '@material-ui/core'
import FileUploader from './components/FileUploader'
import LinkUrlInput from './components/LinkUrlInput'
import CategoryDropdown from './components/CategoryDropdown'
import TagsCreatable from './components/TagsCreatable'
import AudienceCheckboxes from './components/AudienceCheckboxes'
import { useWizardFieldStyles } from './wizardFieldStyles'

const useStyles = makeStyles((theme) => ({
	heading: {
		fontWeight: 600,
		color: theme.palette.text.primary,
		marginBottom: theme.spacing(0.5)
	},
	subtitle: {
		marginBottom: theme.spacing(2),
		color: theme.palette.text.secondary,
		display: 'block'
	},
	field: {
		marginBottom: theme.spacing(2)
	},
	currentFileRow: {
		display: 'flex',
		alignItems: 'center',
		gap: theme.spacing(2),
		padding: theme.spacing(1.5),
		marginBottom: theme.spacing(2),
		border: `1px solid ${theme.palette.divider}`,
		borderRadius: theme.shape.borderRadius
	},
	currentFileLabel: {
		flex: 1,
		color: theme.palette.text.primary
	},
	replacementCaption: {
		color: theme.palette.text.secondary,
		fontStyle: 'italic',
		marginBottom: theme.spacing(1)
	}
}))

const TYPE_LABEL = {
	document: 'Document',
	image: 'Image',
	video: 'Video',
	link: 'Link'
}

const Step2Data = ({ state, dispatch, isEditMode }) => {
	const classes = useStyles()
	const fieldClasses = useWizardFieldStyles()
	const isLink = state.type === 'link'

	const renderFileSection = () => {
		// Edit mode, non-Link, not replacing → show the "Current file:"
		// row with a Replace button. Submitting in this state preserves
		// the existing FilePath on the BE side.
		if (isEditMode && !isLink && !state.isReplacing) {
			return (
				<Box className={classes.currentFileRow}>
					<Typography variant='body2' className={classes.currentFileLabel}>
						<strong>Current file:</strong> {state.originalFilename || '(unknown)'}
					</Typography>
					<Button
						variant='outlined'
						size='small'
						onClick={() => dispatch({ type: 'START_REPLACING' })}
					>
						Replace file
					</Button>
				</Box>
			)
		}

		// Edit mode, non-Link, replacing → show the FileUploader plus a
		// "Cancel replacement" button that reverts to the current-file row.
		if (isEditMode && !isLink && state.isReplacing) {
			return (
				<>
					<Typography variant='caption' className={classes.replacementCaption}>
						Pick a new file or cancel to keep the current one.
					</Typography>
					<FileUploader
						type={state.type}
						file={state.file}
						error={state.fieldErrors.file || state.fileError}
						onFileSelected={(file, error) => dispatch({ type: 'SET_FILE', value: file, error })}
					/>
					<Box marginBottom={2}>
						<Button
							size='small'
							onClick={() => dispatch({ type: 'CANCEL_REPLACING' })}
						>
							Cancel replacement
						</Button>
					</Box>
				</>
			)
		}

		// Create mode (or Link type, which never has a file). Standard uploader.
		return (
			<FileUploader
				type={state.type}
				file={state.file}
				error={state.fieldErrors.file || state.fileError}
				onFileSelected={(file, error) => dispatch({ type: 'SET_FILE', value: file, error })}
			/>
		)
	}

	return (
		<>
			<Typography variant='subtitle1' className={classes.heading}>
				Resource data
			</Typography>
			<Typography variant='caption' className={classes.subtitle}>
				Selected type: {TYPE_LABEL[state.type] || ''}
			</Typography>

			{isLink ? (
				<LinkUrlInput
					value={state.externalUrl}
					error={state.fieldErrors.externalUrl}
					onChange={(v) => dispatch({ type: 'SET_URL', value: v })}
				/>
			) : (
				renderFileSection()
			)}

			<TextField
				className={`${classes.field} ${fieldClasses.textField}`}
				fullWidth
				variant='outlined'
				margin='dense'
				label='Title'
				placeholder='E.g. Manual VR27 R2 HUBZ'
				value={state.title}
				onChange={(e) => dispatch({ type: 'SET_TITLE', value: e.target.value })}
				error={Boolean(state.fieldErrors.title)}
				helperText={state.fieldErrors.title || ''}
				inputProps={{ maxLength: 255 }}
			/>

			<CategoryDropdown
				value={state.categoryId}
				error={state.fieldErrors.categoryId}
				onChange={(id) => dispatch({ type: 'SET_CATEGORY', value: id })}
			/>

			<AudienceCheckboxes
				audience={state.audience}
				error={state.fieldErrors.audience}
				onToggleRole={(role) => dispatch({ type: 'TOGGLE_AUDIENCE', role })}
				onToggleAll={() => dispatch({ type: 'TOGGLE_ALL_AUDIENCE' })}
			/>

			<TagsCreatable
				selectedTags={state.tags}
				onAddTag={(tag) => dispatch({ type: 'ADD_TAG', tag })}
				onRemoveTag={(tagId) => dispatch({ type: 'REMOVE_TAG', tagId })}
			/>
		</>
	)
}

Step2Data.propTypes = {
	state: propTypes.object.isRequired,
	dispatch: propTypes.func.isRequired,
	isEditMode: propTypes.bool
}

Step2Data.defaultProps = {
	isEditMode: false
}

export default Step2Data
